<template>
  <div
    class="dk-page-header dk-page-header--big-hero"
    v-if="formPage && formPageId"
    ref="header"
  >
    <DKBigHeroHeader ref="bigheroHeader" :genericPage="formPage" />

    <div class="grid grid-cols-8 bg-white px-4 lg:grid-cols-16 lg:px-0">
      <div class="col-span-8 pt-4 lg:col-span-10 lg:col-start-2 lg:pb-8">
        <NoSSR>
          <iframe
            width="100%"
            :src="formPage.formUrl"
            :title="formPage.title"
            ref="jotformIframe"
          ></iframe>
        </NoSSR>
      </div>

      <div
        class="col-span-6 col-start-2 pb-4 lg:col-span-4 lg:col-start-12 lg:py-8"
      >
        <DKLinkBoxList :linkList="formPage.links"></DKLinkBoxList>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Vue, Component } from 'vue-facing-decorator';
import {
  DKButton,
  DKLinkBoxList,
  DKBigHeroHeader,
} from '@dormakaba/dormakaba-components';
import { useActiveMeta, useMeta } from 'vue-meta';
import { generateMetaInfo } from '@/helpers/metainfo';
import NoSSR from '@/components/NoSSR.vue';
import { DKFormPage } from '@/models/DKFormPage';
import { DKMetaInfo } from '@/models/DKMetaInfo';
import { FooterStatus } from '@/store/app/models/FooterStatus';

@Component({
  components: {
    DKButton,
    DKLinkBoxList,
    NoSSR,
    DKBigHeroHeader,
  },
})
export default class FormPage extends Vue {
  get metaInfo(): DKMetaInfo {
    return {
      meta: (this.$metaInfoRef as any).meta,
      page: this.formPage,
      locale: this.$i18n.locale,
      $route: this.$route,
    };
  }

  get formPageId(): string | null {
    return this.$store.getters['navigation/pageIdBySlug'](
      this.$route.params.slug
    );
  }

  get formPage(): DKFormPage {
    return this.$store.getters['content/formPage'];
  }

  created() {
    this.$metaInfoRef = useMeta({ ...useActiveMeta() });
  }

  receiveMessage(event) {
    if (event.origin === 'https://form.jotform.com') {
      const messageParts = event.data.split(':');
      if (messageParts[0] === 'setHeight' && messageParts.length === 3) {
        const height = parseInt(messageParts[1], 10);
        if (!isNaN(height)) {
          this.$refs.jotformIframe.style.height = `${height}px`;
        }
      }
    }
  }

  beforeDestroy() {
    window.removeEventListener('message', this.receiveMessage);
  }

  async mounted() {
    await this.$store.dispatch('content/fetchFormPage', {
      id: this.formPageId,
      locale: this.$i18n.locale,
      $route: this.$route,
      $router: this.$router,
    });

    this.$route.meta.defaultFooter = FooterStatus.FOOTER;

    const meta = generateMetaInfo(this.metaInfo);

    this.$trackView('form_page', meta.title);

    this.$store.commit('navigation/setNavbarTransparentElement');

    window.addEventListener('message', this.receiveMessage, false);
  }

  async serverPrefetch() {
    return Promise.all([
      this.$store.dispatch('content/fetchFormPage', {
        id: this.formPageId,
        locale: this.$i18n.locale,
        $route: this.$route,
        $router: this.$router,
      }),
    ]).then(() => generateMetaInfo(this.metaInfo));
  }

  onGoBack() {
    this.$router.back();
  }
}
</script>
<style scoped lang="postcss">
iframe {
  width: 100%;
  height: 100%;
  border: none;
  overflow: hidden;
  border-radius: 8px;

  @screen lg {
    min-height: 1600px;
  }
}

.dk-page-header {
  @apply relative;
}

:deep(.background-media-asset__img),
:deep(.background-media-asset__video) {
  @apply absolute left-0 top-0;
  z-index: -1;
}

:deep(.dk-link-box-list) {
  @apply pb-10 pt-24 md:pt-58;
}

:deep(ul.page-section) {
  padding: 0 !important;
}
</style>
